<template lang="pug">
  .agency-photo-picker.ma-0
    Dialog(
      eager
      persistent
      scrollable
      v-model="dialog.dialog.isActive.value"
      max-width="80%"
      :title="dialogTitle"
      @close="close"
    )
      template(v-slot:dialog-content)
        AgencyListToolbar(
          v-model="filterModel"
          :selected-tab="selectedTab"
          :agency-content-types="agencyContentTypes"
          :content-types="getContentTypesOfTab"
        )
        .row(v-if="!dataLoadParameters.isLoadingFirstPage.value")
          .col.col-md-2.py-0.my-4(v-for="(image, index) in lazyValue" :key="index")
            v-card.cover
              ImagePreview(
                :src="image.path"
                :image-preview-size="imagePreviewSize"
                :aspect-ratio="1"
                customClass="mx-2 my-2"
                :show-preview="true"
              )
                template(v-slot:card-actions)
                  v-row(no-gutters).d-flex
                    span.date-limit {{ formattedCreatedDate(image.createdDate) }}
                  v-card-title.pa-0.mt-2
                    v-tooltip(bottom)
                      template(v-slot:activator="{ on, attrs }")
                        p.mb-0.category-limit(v-on="on" v-bind="attrs")
                          span {{ image.agency.name }}
                          | !{' '}
                          span {{ getCategoryName(image) }}
                      span {{ getCategoryName(image) }}
                  v-card-text.mb-5.pa-0
                    v-tooltip(bottom)
                      template(v-slot:activator="{ on, attrs }")
                        p.limited-title(v-on="on" v-bind="attrs")
                          span {{ image.title }}
                      span {{ image.title }}
                  .justify-center.d-flex
                    PhoCheckbox(v-model="lazyValue[index].isSelected" @input="onSelectionChanged(index, $event)")
        .row(v-if="dataLoadParameters.isLoadingFirstPage.value")
          .col.col-md-2.py-0.my-4(v-for="i in 50")
            v-skeleton-loader.v-sheet.v-card.news-card(type="image, article, button@1")
        v-btn.ma-2.white--text.d-block.mx-auto.mb-6(
          v-if="!dataLoadParameters.isLoadingFirstPage.value"
          large
          color="grey darken-3"
          :loading="dataLoadParameters.isFetching.value"
          :disabled="dataLoadParameters.isAllLoaded.value"
          @click="showMore"
        )
          v-icon(left dark) | {{ dataLoadParameters.showMoreButton.icon.value }}
          | {{ dataLoadParameters.showMoreButton.text.value }}
      template(v-slot:dialog-actions)
        .d-flex.justify-end.align-center
          PhoCheckbox.mr-3(v-model="dialog.lazyValue.value.addPhotoInformation" :label="$t($lang.agency.ADD_PHOTO_INFORMATION)"
            v-if="showAddPhotoInformation")
          v-btn.mr-2(@click="selectImages") {{ $t($lang.agency.UPLOAD_AND_CONTINUE) }}
</template>

<script>
import { useContext } from '@/compat';
import { computed, ref, watch } from 'vue';
import { omit } from 'ramda';
import { hashCode } from '@piri/utils';
import { distinctArray } from '@/helpers/misc';
import { displayDateTimeFormatWithDay, formatDateTime } from '@/helpers/misc/date';
import { useInfiniteFetch } from '@/helpers/misc/pagination';
import { useAlert, useConfirm, usePromisifiedDialog } from '@/helpers/misc/dialog';
import { useOverlaySpinnerLoading } from '@/helpers/loadings';
import { getAgencyArchives } from '@/api/agency-archive/AgencyArchiveApi';
import { getAgencyContents } from '@/api/agency/AgencyContentApi';
import ImagePreview from '@/components/misc/media/preview/ImagePreview.vue';
import { PhoCheckbox } from '@/components/misc/form';
import { Dialog } from '../misc/dialog';
import AgencyListToolbar from './AgencyListToolbar.vue';
import { agencyPhotoPickTypes, agencyTabItems } from './constants';

export default {
  name: 'AgencyPhotoPicker',
  components: {
    Dialog,
    AgencyListToolbar,
    PhoCheckbox,
    ImagePreview,
  },
  props: {
    itemsPerPage: {
      type: Number,
      required: true,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    showAddPhotoInformation: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const { $i18n, $lang } = useContext();
    const alert = useAlert();
    const confirm = useConfirm();
    const newFilterModel = ref({});

    const contentTypes = ref({
      [agencyPhotoPickTypes.AGENCY]: ['news', 'photo-gallery', 'video'],
      [agencyPhotoPickTypes.ARCHIVE]: [],
    });
    const agencyContentTypes = ['news', 'photo-gallery', 'infographic', 'video'];
    const pickType = ref(agencyPhotoPickTypes.AGENCY);
    const selectedTab = ref(agencyTabItems['photo-gallery']);
    const loadingHashCode = hashCode('agencyPhotoPick');
    const filterModel = ref({});
    const firstCreatedDate = ref(null);

    const dialog = usePromisifiedDialog({
      files: [],
      addPhotoInformation: false,
    });

    const dialogTitle = computed(() => (
      pickType.value === agencyPhotoPickTypes.ARCHIVE
        ? $i18n.t($lang.agency.SELECT_THE_IMAGES_YOU_WANT_TO_BRING_FROM_THE_ARCHIVE)
        : $i18n.t($lang.agency.SELECT_THE_IMAGES_YOU_WANT_TO_BRING_FROM_THE_AGENCY)
    ));

    const lazyValue = ref([]);
    const formattedCreatedDate = createdDate => formatDateTime(createdDate, displayDateTimeFormatWithDay);

    const isArchiveTab = computed(() => pickType.value === agencyPhotoPickTypes.ARCHIVE);

    const fetchPhotos = (page, itemsPerPage) => {
      newFilterModel.value = {
        ...filterModel.value,
        ...(!isArchiveTab.value ? { contentTypes: ['photo-gallery', 'infographic'] } : {}),
      };

      if (!isArchiveTab.value) {
        newFilterModel.value.tabName = 'photo-gallery';
      }

      return (isArchiveTab.value ? getAgencyArchives : getAgencyContents)(
        {
          filter: newFilterModel.value,
          page,
          limit: itemsPerPage,
          firstCreatedDate: page === 1 ? '' : firstCreatedDate.value,
        },
      );
    };

    const {
      data,
      fetch: fetchData,
      reFetch: reFetchData,
      showMore: showMoreAction,
      dataLoadParameters,
    } = useInfiniteFetch({
      fetch: fetchPhotos,
      itemsPerPage: props.itemsPerPage,
      debounce: {
        delay: 500,
        immediate: true,
      },
    });

    const agencyContents = computed(() => distinctArray(data.value, 'id'));

    const fillFirstCreatedDate = () => {
      const firstData = data.value.length > 0 ? agencyContents.value[0] : {};
      firstCreatedDate.value = firstData?.createdDate || null;
    };

    const generateImageData = () => {
      lazyValue.value = data.value.reduce((acc, agencyData) => {
        const draftAcc = [...acc];

        if (!agencyData.images) {
          return draftAcc;
        }

        agencyData.images.forEach(image => {
          if (!draftAcc.some(accImage => accImage.path === image.path)) {
            draftAcc.push({
              ...image,
              isSelected: (dialog.lazyValue.value.files || []).some(selectedImage => selectedImage.path === image.path),
              ...omit(['images'], agencyData),
            });
          }
        });

        return draftAcc;
      }, []);
    };

    const clearSelection = exceptedIndex => {
      dialog.lazyValue.value.files.splice(0);

      lazyValue.value.filter(image => image.isSelected).forEach(image => {
        // isNilOrEmpty kullanılmadı çünkü sıfır gelme ihtimali var.
        if (exceptedIndex !== null
          && exceptedIndex !== undefined
          && exceptedIndex === lazyValue.value.findIndex(lazyImage => lazyImage.path === image.path)) {
          return;
        }

        image.isSelected = false;
      });
    };

    const getCategoryName = item => (item.agencyCategoryName ? item.agencyCategoryName[0] : '');

    const fetch = async () => {
      await fetchData();
      fillFirstCreatedDate();
      generateImageData();
    };

    const reFetch = async () => {
      await reFetchData();
      fillFirstCreatedDate();
      generateImageData();
    };

    const showMore = async () => {
      await showMoreAction();
      generateImageData();
    };

    const onSelectionChanged = (index, isSelected) => {
      const selectedImage = lazyValue.value[index];

      if (!selectedImage) {
        return;
      }

      const imageIndexInLazyValue = dialog.lazyValue.value.files.findIndex(image => image.path === selectedImage.path);

      if (imageIndexInLazyValue <= -1 && isSelected) {
        if (!props.multiple) {
          clearSelection(index);
        }

        dialog.lazyValue.value.files.push(omit(['isSelected'], selectedImage));
      } else if (imageIndexInLazyValue >= 0 && !isSelected) {
        dialog.lazyValue.value.files.splice(imageIndexInLazyValue, 1);
      }
    };

    const getContentTypesOfTab = computed(() => contentTypes.value[pickType.value]);

    const pick = async type => {
      useOverlaySpinnerLoading().show(loadingHashCode);
      pickType.value = type;
      selectedTab.value = type === agencyPhotoPickTypes.ARCHIVE ? agencyTabItems.archive : agencyTabItems['photo-gallery'];
      clearSelection();
      await reFetch();
      useOverlaySpinnerLoading().hide(loadingHashCode);

      return dialog.edit({
        files: [],
        addPhotoInformation: false,
      });
    };

    const selectImages = async () => {
      if (!dialog.lazyValue.value.files.length) {
        await alert.error($i18n.t($lang.app.ERROR), $i18n.t($lang.image.PLEASE_SELECT_IMAGE_TO_UPLOAD));

        return;
      }

      useOverlaySpinnerLoading().show(loadingHashCode);
      dialog.save();
      filterModel.value = {};
      useOverlaySpinnerLoading().hide(loadingHashCode);
    };

    const close = async () => {
      if (dialog.lazyValue.value.files.length && !(await confirm.warning()(
        $i18n.t($lang.app.WARNING),
        $i18n.t($lang.image.THE_IMAGES_YOU_SELECTED_WILL_NOT_BE_UPLOADED_ARE_YOU_SURE),
      ))) {
        return;
      }

      dialog.clear();
      dialog.close();
      filterModel.value = {};
    };

    watch(() => filterModel.value, async () => {
      firstCreatedDate.value = null;

      if (dialog.dialog.isActive.value) {
        await reFetch();
      } else {
        data.value.splice(0);
      }
    }, { deep: true });

    return {
      getContentTypesOfTab,
      agencyContentTypes,
      dialog,
      dialogTitle,
      lazyValue,
      formattedCreatedDate,
      getCategoryName,
      close,
      onSelectionChanged,
      pick,
      data,
      fetch,
      reFetch,
      showMore,
      dataLoadParameters,
      selectImages,
      filterModel,
      selectedTab,
      imagePreviewSize: 300,
    };
  },
};
</script>

<style lang="scss" scoped>
.limited-title {
  position: relative;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  height: 42px;
  overflow: hidden;
  color: #606060;
  font-weight: 500;
  font-size: 12px;
  font-style: normal;
  line-height: normal;
  text-overflow: ellipsis;
}

.category-limit {
  position: relative;
  display: -webkit-box;
  max-width: 100%;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  color: #000;
  font-weight: 600;
  font-size: 16px;
  font-style: normal;
  line-height: normal;
}

.city-limit {
  position: relative;
  display: -webkit-box;
  max-width: 120px;
  height: 32px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
}

.date-limit {
  position: relative;
  display: -webkit-box;
  max-width: 180px;
  max-height: 39px;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #606060;
  font-weight: 600;
  font-size: 12px;
  font-style: normal;
  line-height: normal;
}
</style>
